import { CheckIcon, ChevronDownIcon } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

export interface SortDropdownOption {
  value: string
  label: string
}

interface SortDropdownProps {
  value: string
  options: SortDropdownOption[]
  onChange: (value: string) => void
  label?: string
  className?: string
  labelClassName?: string
}

export function SortDropdown({
  value,
  options,
  onChange,
  label = 'Ordenar por',
  className,
  labelClassName
}: SortDropdownProps) {
  const selected = options.find(option => option.value === value)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            // Arcade mechanic (same language as Button.tsx / ChipRail's Chip):
            // solid fill + single 3D "ledge" via bottom box-shadow, hover
            // lift, press/open seats it flush. See design-system/MASTER.md's
            // "SortDropdown" and "ChipRail" entries for the shared spec.
            'inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-[11px] lg:gap-2',
            'border-2 border-primary/22 bg-white px-3 font-hl text-xs font-bold text-primary-dark lg:px-4 lg:text-sm',
            'shadow-[0_3px_0_var(--brand-gray-light)]',
            'transition-[transform,box-shadow] duration-[90ms] ease-[cubic-bezier(0.2,0.8,0.3,1)]',
            'hover:-translate-y-[2px] hover:shadow-[0_5px_0_var(--brand-gray-light)]',
            'data-[state=open]:translate-y-[3px] data-[state=open]:shadow-[inset_0_2px_4px_rgba(0,0,0,0.20)]',
            className
          )}
        >
          <span className={cn('text-primary-dark/70', labelClassName)}>
            {label}
          </span>
          <span>{selected?.label ?? ''}</span>
          <ChevronDownIcon className="size-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
          >
            <span>{option.label}</span>
            {option.value === value ? <CheckIcon className="size-4" /> : null}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
